import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

// Request interceptor - add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor - normalize paginated responses + handle 401 refresh
api.interceptors.response.use(
  (response) => {
    // Normalize: if response has { items, page, pageSize, total }, also set .data.data = items
    if (response.data && Array.isArray(response.data.items) && !response.data.data) {
      response.data.data = response.data.items;
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Extract standardized error message for consumers
    if (error.response?.data?.error) {
      error.userMessage = error.response.data.error.message || 'An error occurred';
      error.errorCode = error.response.data.error.code || 'UNKNOWN';
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          const res = await axios.post(`${API_BASE}/auth/refresh`, {
            refresh_token: refreshToken,
          });
          const { access_token, refresh_token: newRefresh } = res.data;
          localStorage.setItem('access_token', access_token);
          localStorage.setItem('refresh_token', newRefresh);
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
        } catch (refreshError) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      } else {
        localStorage.removeItem('access_token');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

/**
 * Extract user-friendly error message from API error.
 * Usage: catch(e) { toast.error(getErrorMessage(e)); }
 */
export function getErrorMessage(error) {
  if (error.userMessage) return error.userMessage;
  if (error.response?.data?.error?.message) return error.response.data.error.message;
  if (error.response?.data?.detail) {
    if (typeof error.response.data.detail === 'string') return error.response.data.detail;
    return error.response.data.detail.message || JSON.stringify(error.response.data.detail);
  }
  return error.message || 'An unexpected error occurred';
}

export default api;
